import './PaypalECS.css';

import type {AppConfig} from '../initialize';
import type {ButtonStyle} from '../../shared/PaypalECSTypes';
import {initialize} from '../initialize';
import type {RequestFn, SendFn} from '../setupMessages';
import injectScript from './injectScript';
import {getSdk} from './paypalSdk';

let isButtonLoaded: boolean;

// client-id changes dynamically, so passing an integration date ensures that no breaking changes are made to this integration
// https://developer.paypal.com/sdk/js/configuration/#integration-date
const INTEGRATION_DATE = '2022-09-15';

type UrlParams = {
  'client-id': string;
  intent: string;
  currency: string;
  [key: string]: string;
};

const initializeButton = (
  urlParams: UrlParams,
  send: SendFn<'PaypalECS'>,
  request: RequestFn<'PaypalECS'>,
  style?: ButtonStyle,
): void => {
  if (isButtonLoaded) {
    return;
  }

  let url = `https://www.paypal.com/sdk/js?components=buttons&integration-date=${INTEGRATION_DATE}`;
  Object.keys(urlParams).forEach((param) => {
    url += `&${param}=${encodeURIComponent(urlParams[param])}`;
  });

  injectScript(
    url,
    () => {
      isButtonLoaded = true;
      const paypal = getSdk();
      if (!paypal || !paypal.Buttons || !paypal.FUNDING) {
        return;
      }

      paypal
        .Buttons({
          fundingSource: paypal.FUNDING.PAYPAL,
          style,
          // Sets up the transaction when a payment button is clicked
          createOrder: (data, action) => {
            return request({
              event: 'createOrder',
            })
              .then((resp) => {
                if (resp.event === 'setOrder') {
                  return Promise.resolve(resp.orderId);
                }

                if (resp.event === 'createOrderClientSide') {
                  return action.order.create({
                    purchase_units: [
                      {
                        amount: {
                          currency_code: resp.amount.currency,
                          value: resp.amount.amount,
                        },
                      },
                    ],
                  });
                }

                throw Error(
                  `Expected 'setOrder' or 'createOrderClientSide' action in response, got '${resp.event}'`,
                );
              })
              .then((orderId) => orderId);
          },
          onError: (err) => {
            send({
              event: 'onError',
              error: (err as unknown as Error).message,
            });
          },
          onInit: () => {
            send({
              event: 'onInit',
            });
          },
          onClick: () => {
            send({
              event: 'onClick',
            });
          },
          onApprove: () => {
            send({
              event: 'onApprove',
            });
            return Promise.resolve();
          },
          onCancel: () => {
            send({
              event: 'onCancel',
            });
          },
          onShippingChange: (data, actions) => {
            const selectedShippingOption = data?.selected_shipping_option
              ? {
                  // @ts-expect-error - This does appear on the response, just not in the TS types
                  id: data.selected_shipping_option.id,
                  label: data.selected_shipping_option.label,
                  type: data.selected_shipping_option.type.toLowerCase() as
                    | 'shipping'
                    | 'pickup',
                  amount: {
                    currency:
                      data.selected_shipping_option.amount.currency_code,
                    amount: data?.selected_shipping_option.amount.value,
                  },
                }
              : undefined;

            return request({
              event: 'onShippingChange',
              orderId: data?.orderID,
              shippingAddress: {
                city: data?.shipping_address?.city,
                state: data?.shipping_address?.state,
                countryCode: data?.shipping_address?.country_code,
                postalCode: data?.shipping_address?.postal_code,
              },
              selectedShippingOption,
            }).then((response) => {
              if (response.event === 'rejectShipping') {
                return actions.reject();
              }

              return actions.resolve();
            });
          },
        })
        .render('#paypal-button-container');
    },
    () => {
      send({
        event: 'onError',
        error: `Error loading PayPal script ${url}`,
      });
    },
  );
};

const app = (config: AppConfig<'PaypalECS'>) => {
  config.subscribe((messageData) => {
    if (messageData.event === 'loadButton') {
      const params: UrlParams = {
        'client-id': messageData.clientId,
        intent: messageData.intent,
        currency: messageData.currency,
      };

      if (messageData.merchantId) {
        params['merchant-id'] = messageData.merchantId;
      }
      if (messageData.locale) {
        params.locale = messageData.locale;
      }
      initializeButton(params, config.send, config.request, messageData.style);
    }
  });
};

initialize({
  app,
  owner: 'checkout',
});
